/** Annotation progress colours, shared by the Scan Dashboard toggle and the A/B
 * picker so the two surfaces cannot disagree about what a colour means.
 *
 * NO_RUNS  no runs of the active variant - nothing to annotate, someone must run it
 * NONE     runs exist, this admin has annotated none
 * PARTIAL  some annotated
 * COMPLETE every match diverging by 2+ ranks has at least one run annotated
 *
 * Explicit hex because MUI has no yellow and its `warning` IS orange, so NO_RUNS
 * and NONE would have collided on palette tokens.
 */
export const ANNOTATION_COLOURS = {
  NO_RUNS: '#e65100',
  NONE: '#fbc02d',
  PARTIAL: '#1976d2',
  COMPLETE: '#2e7d32',
} as const

export type AnnotationStatus = 'none' | 'partial' | 'complete'

/** A scan MISSING from the annotation-status map has no runs of the active
 * variant, which is why `undefined` maps to NO_RUNS rather than to "nothing done".
 * One function, used by both the Scan Dashboard toggle and the A/B picker chip, so
 * the same scan cannot show two different colours on two pages.
 */
export const annotationColour = (
  status: AnnotationStatus | undefined
): string => {
  switch (status) {
    case 'none':
      return ANNOTATION_COLOURS.NONE
    case 'partial':
      return ANNOTATION_COLOURS.PARTIAL
    case 'complete':
      return ANNOTATION_COLOURS.COMPLETE
    default:
      return ANNOTATION_COLOURS.NO_RUNS
  }
}

export const annotationLabel = (
  status: AnnotationStatus | undefined
): string => {
  switch (status) {
    case 'none':
      return 'No annotations yet on this scan'
    case 'partial':
      return 'Partly annotated - some divergent matches still unjudged'
    case 'complete':
      return 'Every match diverging by 2+ ranks has been annotated'
    default:
      return 'No runs of the active variant yet - this scan needs a run'
  }
}

/** Yellow and orange are dark-on-light; the rest need white text. */
export const annotationTextColour = (
  status: AnnotationStatus | undefined
): string => (status === 'none' ? 'rgba(0, 0, 0, 0.87)' : '#fff')


// GH #1723 / fs-978.11: admin A/B comparison of production vs shadow match runs.

// The production arm is keyed by this literal in `MatchComparisonItem.arms`
// every other key is a match_run id as a string.
export const PRODUCTION_ARM = 'production'

// What a human sees for that arm. The key above stays 'production' because the
// client keys arm cells on it; only the label changes.
export const PRODUCTION_ARM_FALLBACK_LABEL = 'gpt-4.1 / baseline'

export interface MatchRun {
  id: number
  label: string | null
  stage: 'stage_1' | 'stage_2'
  model: string
  prompt_version: string
  status: string
  error: string | null
  items_total: number
  items_completed: number
  items_failed: number
  cost_usd: number | null
  context_fingerprint: string | null
  trigger: string | null
  created_by: string | null
  created_at: string
  finished_at: string | null
  item_count: number | null
  total_latency_ms: number | null
  avg_latency_ms: number | null
  error_count: number | null
  prompt_tokens: number | null
  cached_tokens: number | null
  completion_tokens: number | null
}

export interface StrategyRepeat {
  match_run_result_id: number | null
  match_run_id: number
  run_label: string | null
  rank: number | null
  certainty: string | null
  message: string | null
  reasoning: string | null
  cost_usd: number | null
  // True when priced from tokens because the provider reported no cost.
  cost_is_estimated?: boolean
  latency_ms: number | null
  error: string | null
}

/** A strategy is (model, prompt_version); a run is one execution of it. */
export interface MatchStrategy {
  key: string
  model: string
  prompt_version: string
  label: string
  run_count: number
  run_ids: number[]
  run_labels: Array<string | null>
  // Totals across every repeat, with the per-run mean beside them.
  total_cost_usd: number | null
  mean_cost_per_run_usd: number | null
  // True when the provider reported no cost and the figure was priced from
  // recorded tokens. gpt-4.1 runs record cost_usd = 0 but carry full token counts.
  cost_is_estimated: boolean
  // Summed model time across every call, not wall clock: the runner is concurrent.
  total_latency_ms: number | null
  mean_latency_per_run_ms: number | null
  error_count: number
  // False for a single-run strategy: one run cannot measure its own stability.
  stability_measured: boolean
  // In-flight runs. Their results are deliberately NOT in the comparison cells -
  // a half-finished run would give a stability reading over whatever landed first.
  is_running: boolean
  running_runs: number
  progress_done: number
  progress_total: number
  // Claims to be running but has written nothing for a long time - almost always a
  // process that died mid-run. The client stops polling on it.
  is_stalled: boolean
  // A launch that died. Without surfacing these the page reads "0 runs" as though
  // nothing was ever started.
  is_failed: boolean
  failed_runs: number
  last_error: string | null
}

export type Agreement = 'agrees' | 'differs' | 'unstable' | null

export interface MatchArmCell {
  arm: string
  match_run_result_id: number | null
  scan_result_id: number | null
  // The modal rank across this strategy's repeats - the representative integer,
  // and what an annotation attaches to.
  rank: number | null
  // The average across the repeats. This is what the cell SHOWS once they
  // diverge; a strategy that returned 1, 3, 3 reads better as 2.3 than as a flat 3.
  mean_rank: number | null
  // Production only: the rank the customer was actually served.
  live_rank?: number | null
  rank_low: number | null
  rank_high: number | null
  // True when every repeat landed on the same rank.
  stable: boolean
  // 'unstable' means the repeats straddle production, so this row is not evidence
  // either way - distinct from a strategy that consistently differs.
  agreement: Agreement
  repeat_count: number
  repeats?: StrategyRepeat[]
  // Index of the repeat the summarised text came from, so the chip showing on
  // load is the highlighted one.
  representative_repeat_index?: number
  score?: number | null
  certainty: string | null
  // The customer-facing explanation: `customer_message` where the prompt splits
  // it out, otherwise the reasoning text.
  message: string | null
  reasoning: string | null
  questions: string | null
  // The place this item takes in the list this arm would present. Derived, not
  // stored - stage 1 records a 1-5 category, and the order comes from it.
  position?: number
  cost_usd: number | null
  // True when the cell's cost was priced from tokens rather than reported.
  cost_is_estimated?: boolean
  // Production only: the scan total shared across its items. An ESTIMATE - the
  // cost table records one row per workflow for the whole scan, not per item.
  cost_usd_estimated?: number
  latency_ms: number | null
  error: string | null
  reused?: boolean
  critique_needed_revision?: boolean | null
}

export interface MatchFeedbackRow {
  id: number
  scan_result_id: number | null
  match_run_result_id: number | null
  opportunity_uuid: string
  verdict: string
  note: string | null
  admin_name: string | null
  created_at: string
}

export interface MatchComparisonItem {
  opportunity_uuid: string
  fund_name: string | null
  funder_name: string | null
  scan_result_id: number | null
  arms: Record<string, MatchArmCell>
  feedback: MatchFeedbackRow[]
  rank_spread: number
  // False when some displayed strategy produced no result for this opportunity.
  comparable: boolean
  disagrees_with_production: boolean
  // At least one challenger's range overlaps the incumbent's.
  unstable: boolean
  // The incumbent disagreed with ITSELF across its replays on this item.
  production_unstable: boolean
  missing_arms: string[]
}

/** One run's counters, as carried by the progress stream. Never results. */
export interface RunProgress {
  id: number
  key: string
  label: string | null
  status: string
  items_total: number | null
  items_completed: number | null
  items_failed: number | null
  done: number
  rows_written: number
  stalled: boolean
  error: string | null
}

export interface MatchComparison {
  need_id: number
  batch_number: number
  production_arm: string
  production_arm_label: string
  // The incumbent strategy behind the production column: the live result plus
  // every replay of the same model+prompt.
  incumbent: MatchStrategy | null
  // The strategy a shadow run uses when the admin does not name one.
  default_strategy: { model: string; prompt: string }
  // The strategies shown in the grid (capped by default).
  strategies: MatchStrategy[]
  // Every stage-1 strategy on this scan, for the picker.
  available_strategies: MatchStrategy[]
  // The incumbent's spend on this scan. Null when the scan predates scan_ai_costs
  // - shown as "not recorded", never as $0.00.
  production_cost: ProductionCost | null
  funding_need: FundingNeed | null
  same_inputs: boolean
  context_fingerprints: string[]
  items: MatchComparisonItem[]
  totals: {
    items: number
    // Items every displayed strategy ran. Ranks and positions compare over these.
    comparable_items: number
    // Confident: every repeat of some strategy differed from production.
    disagreements: number
    // Ranges overlap the incumbent's - noise, counted separately on purpose.
    unstable: number
    // Items where the incumbent's own replays disagree with each other.
    production_unstable: number
  }
}

export interface FundingNeed {
  id: number
  title: string | null
  description: string
  // The assembled text the model actually reads - the version worth judging a
  // disagreement against, not just the raw description.
  profile_text: string | null
  timing_criteria: string | null
  min_amount: number | null
  amount: number | null
  accepts_gambling_proceeds: boolean
  accepts_international: boolean
  use_need_context_only: boolean
  is_archived: boolean
  org_name: string
  org_uuid: string
}

export interface ProductionCost {
  total_usd: number
  calls: number
  prompt_tokens: number
  cached_tokens: number
  completion_tokens: number
  by_workflow: Array<{
    workflow: string
    model: string
    calls: number
    cost_usd: number
  }>
  per_item_estimate_usd?: number
  // True when scan_ai_costs had nothing and this came from the incumbent's
  // replays at their own per-match rate.
  estimated_from_replays?: boolean
}

export interface OpportunityContext {
  uuid: string
  fund_name: string | null
  description: string | null
  criteria: string | null
  exclusions: string | null
  timing: string | null
  amount: string | null
  min_amount: number | null
  max_amount: number | null
  no_min_max_amount: boolean
  application_link: string | null
  // The assembled text the model actually reads.
  profile_text: string | null
  always_open: boolean
  is_relational: boolean
  gambling_proceeds: boolean
  is_international: boolean
  only_funds_registered_charities: boolean
  max_rank_is_4: boolean
  tags: string[] | null
  funder_uuid: string
  funder_name: string
  funder_website: string | null
  funder_description: string | null
}

export interface ActiveStrategy {
  backend?: string
  available?: boolean
  key: string
  model: string
  prompt: string
  prompt_version: string
  label: string
  note: string
  // The strategy used when a run is launched without naming one. Not a verdict.
  is_default: boolean
  cost: {
    per_item_usd: number
    mean_latency_ms: number | null
    basis: string
    sample_items: number
  } | null
}

export interface RunEstimate {
  backend?: string
  items: number
  runs: number
  calls: number
  per_item_usd: number | null
  total_usd: number | null
  estimated_seconds: number | null
  basis: string | null
  sample_items: number | null
  // The mean token profile the price came from, so the figure can be checked.
  tokens: { prompt: number; cached: number; completion: number } | null
  // True when the figure came from prompt size rather than measured runs. It runs
  // low, because the assembled prompt carries context this cannot see.
  is_floor: boolean
}

export interface ScanWithRuns {
  need_id: number
  batch_number: number
  org_uuid: string
  org_name: string
  need_title: string
  // Complete stage-1 runs of a currently active strategy - the same population the
  // Repeats column on the comparison table sums to. Stage-2 grader runs are out of
  // scope for this release and are not counted anywhere on this page.
  run_count: number
  // Someone has asked for this scan to be annotated; it sorts to the top.
  annotation_requested: boolean
  annotation_requested_by: string | null
  // Verdicts on this batch. Two numbers because "somebody has judged this" and
  // "I have judged this" send a reviewer to different scans.
  my_annotations: number
  all_annotations: number
  cost_usd: number | null
  last_run_at: string
  // The baseline scan's own date, so the matches dialog can be opened from here
  // with the same header it shows on the Scan Dashboard.
  scan_date: string | null
  // Only present when the listing includes scans with no runs yet.
  item_count?: number
}

export interface MatchRunContext {
  id: number
  label: string | null
  context_fingerprint: string | null
  context_snapshot: Record<string, unknown> | null
}

export const VERDICTS = [
  { value: 'correct_high', label: 'Correct' },
  { value: 'too_high', label: 'Too high' },
  { value: 'too_low', label: 'Too low' },
  { value: 'should_be_excluded', label: 'Exclude' },
  { value: 'uncertain_fit', label: 'Unsure' },
] as const
